<template>
  <div class="bg p-3 pb-20 mb-auto h-full overflow-auto" id="style-1">
    <!-- Global Loading Overlay -->
    <div
      v-if="loading"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
    >
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
    </div>

    <div class="container mx-auto">
      <!-- หัวข้อและแถบค้นหา -->
      <div class="mb-6">
        <h1 class="text-2xl font-semibold text-indigo-300 mb-4">แดชบอร์ดแอดมิน: ผู้เล่นทั้งหมด</h1>
        <div class="flex flex-col sm:flex-row gap-4">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="ค้นหาด้วยชื่อ, อีเมล, หรือ UID"
            :disabled="loading"
            class="flex-1 p-2 rounded-md bg-gray-700 text-indigo-200 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            @input="debouncedSearch"
          />
          <select
            v-model="adminFilter"
            :disabled="loading"
            class="p-2 rounded-md bg-gray-700 text-indigo-200 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="">ทั้งหมด</option>
            <option value="true">แอดมิน</option>
            <option value="false">ไม่ใช่แอดมิน</option>
          </select>
          <select
            v-model="characterFilter"
            :disabled="loading"
            class="p-2 rounded-md bg-gray-700 text-indigo-200 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="">ทั้งหมด</option>
            <option value="true">สร้างตัวละครแล้ว</option>
            <option value="false">ยังไม่สร้างตัวละคร</option>
          </select>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="text-red-300 text-sm text-center mb-4">
        {{ error }}
      </div>

      <!-- ตารางผู้ใช้ -->
      <div class="overflow-x-auto bg-gray-800 rounded-md border border-gray-600">
        <table class="w-full text-left text-indigo-200">
          <thead class="bg-gray-900">
            <tr>
              <th class="p-3 text-sm font-semibold">อัปเดทล่าสุด</th>
              <th class="p-3 text-sm font-semibold">อีเมล</th>
              <th class="p-3 text-sm font-semibold">IP</th>
              <th class="p-3 text-sm font-semibold">UID</th>
              <th class="p-3 text-sm font-semibold">แอดมิน</th>
              <th class="p-3 text-sm font-semibold">ตัวละคร</th>
              <th class="p-3 text-sm font-semibold">เครดิต</th>
              <th class="p-3 text-sm font-semibold">cash</th>
              <th class="p-3 text-sm font-semibold">สถานะ</th>
              <th class="p-3 text-sm font-semibold">การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in filteredUsers"
              :key="user.uid"
              class="border-b border-gray-700 hover:bg-gray-700 transition-colors duration-200"
            >
              <td class="p-3 text-sm">{{ formatTimestamp(user.updated) }}</td>
              <td class="p-3 text-sm">{{ user.email || 'N/A' }}</td>
              <td class="p-3 text-sm">
                <span
                  :class="{ 'text-red-400 cursor-pointer': user.ipCount > 1, 'cursor-pointer': user.ipCount > 1 }"
                  @click="user.ipCount > 1 ? openIpUsersModal(user.ip) : null"
                >
                  {{ user.ip || 'N/A' }}
                </span>
              </td>
              <td class="p-3 text-sm">{{ user.uid }}</td>
              <td class="p-3 text-sm">{{ user.admin ? 'ใช่' : 'ไม่' }}</td>
              <td class="p-3 text-sm">{{ user.characterCreated ? 'ใช่' : 'ไม่' }}</td>
              <td class="p-3 text-sm">{{ user.credit || 0 }}</td>
              <td class="p-3 text-sm">{{ user.cash || 0 }}</td>
              <td class="p-3 text-sm">{{ user.ban ? 'ถูกแบน' : 'ปกติ' }}</td>
              <td class="p-3 flex gap-2 flex-wrap">
                <button
                  v-if="!user.ban"
                  :disabled="loading"
                  class="bg-red-600 text-white px-3 py-1 rounded-md text-sm hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
                  @click="openBanModal(user.uid, user.name || 'N/A')"
                >
                  <i class="fas fa-ban mr-1 text-xs"></i> แบน
                </button>
                <button
                  v-else
                  :disabled="loading"
                  class="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                  @click="openUnbanModal(user.uid, user.name || 'N/A')"
                >
                  <i class="fas fa-check mr-1 text-xs"></i> ปลดแบน
                </button>
                <button
                  :disabled="loading"
                  class="bg-indigo-600 text-white px-3 py-1 rounded-md text-sm hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50"
                  @click="openAddCashModal(user.uid, user.name || 'N/A', user.cash || 0)"
                >
                  <i class="fas fa-money-bill-wave mr-1 text-xs"></i> ปรับเงิน
                </button>
                <button
                  v-if="user.characterCreated"
                  :disabled="loading"
                  class="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                  @click="viewCharacter(user.uid)"
                >
                  <i class="fas fa-user mr-1 text-xs"></i> ดูตัวละคร
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ถ้าไม่มีผู้ใช้ -->
      <div v-if="!filteredUsers.length && !loading" class="text-center text-indigo-200 mt-6">
        ไม่พบผู้ใช้ที่ตรงกับเงื่อนไข
      </div>

      <!-- Modal แสดงผู้ใช้ที่มี IP เดียวกัน -->
      <div
        v-if="showIpUsersModal"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-gray-800 p-6 rounded-lg text-white w-full max-w-md border border-indigo-400">
          <h2 class="text-lg font-semibold text-indigo-300 mb-4">ผู้ใช้ที่มี IP: {{ selectedIp }}</h2>
          <ul class="text-indigo-200 text-sm mb-4">
            <li v-for="user in ipUsers" :key="user.uid" class="mb-2">
              {{ user.name || 'N/A' }} (UID: {{ user.uid }}, อีเมล: {{ user.email || 'N/A' }})
            </li>
          </ul>
          <div class="flex justify-end">
            <button
              class="bg-gray-600 px-4 py-2 rounded-md text-sm hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
              @click="showIpUsersModal = false"
              :disabled="loading"
            >
              ปิด
            </button>
          </div>
        </div>
      </div>

      <!-- Modal ยืนยันการแบน -->
      <div
        v-if="showBanModal"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-gray-800 p-6 rounded-lg text-white w-full max-w-md border border-indigo-400">
          <h2 class="text-lg font-semibold text-indigo-300 mb-4">ยืนยันการแบน</h2>
          <p class="text-indigo-200 text-sm">คุณต้องการแบนผู้ใช้ <strong>{{ banUserName }}</strong> (UID: {{ banUserId }}) หรือไม่?</p>
          <div class="mt-6 flex justify-end gap-4">
            <button
              class="bg-gray-600 px-4 py-2 rounded-md text-sm hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
              @click="showBanModal = false"
              :disabled="loading"
            >
              ยกเลิก
            </button>
            <button
              class="bg-red-600 px-4 py-2 rounded-md text-sm hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
              @click="confirmBan"
              :disabled="loading"
            >
              <span v-if="!loading">แบน</span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
          </div>
        </div>
      </div>

      <!-- Modal ยืนยันการปลดแบน -->
      <div
        v-if="showUnbanModal"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-gray-800 p-6 rounded-lg text-white w-full max-w-md border border-indigo-400">
          <h2 class="text-lg font-semibold text-indigo-300 mb-4">ยืนยันการปลดแบน</h2>
          <p class="text-indigo-200 text-sm">คุณต้องการปลดแบนผู้ใช้ <strong>{{ banUserName }}</strong> (UID: {{ banUserId }}) หรือไม่?</p>
          <div class="mt-6 flex justify-end gap-4">
            <button
              class="bg-gray-600 px-4 py-2 rounded-md text-sm hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
              @click="showUnbanModal = false"
              :disabled="loading"
            >
              ยกเลิก
            </button>
            <button
              class="bg-green-600 px-4 py-2 rounded-md text-sm hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
              @click="confirmUnban"
              :disabled="loading"
            >
              <span v-if="!loading">ปลดแบน</span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
          </div>
        </div>
      </div>

      <!-- Modal ปรับเงิน -->
      <div
        v-if="showAddCashModal"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-gray-800 p-6 rounded-lg text-white w-full max-w-md border border-indigo-400">
          <h2 class="text-lg font-semibold text-indigo-300 mb-4">ปรับเงินให้ผู้ใช้</h2>
          <p class="text-indigo-200 text-sm mb-4">
            ผู้ใช้: <strong>{{ addCashUserName }}</strong> (UID: {{ addCashUserId }})<br>
            เงินปัจจุบัน: {{ addCashCurrent }} บาท
          </p>
          <div class="mb-4">
            <label class="block text-indigo-200 text-sm mb-2">จำนวนเงินที่ต้องการปรับ (ค่าลบเพื่อหักเงิน, บาท):</label>
            <input
              v-model.number="addCashAmount"
              type="number"
              placeholder="กรอกจำนวนเงิน"
              class="w-full p-2 rounded-md bg-gray-700 text-indigo-200 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              :disabled="loading"
            />
            <p v-if="addCashError" class="text-red-300 text-xs mt-2">{{ addCashError }}</p>
          </div>
          <div class="flex justify-end gap-4">
            <button
              class="bg-gray-600 px-4 py-2 rounded-md text-sm hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
              @click="showAddCashModal = false"
              :disabled="loading"
            >
              ยกเลิก
            </button>
            <button
              class="bg-indigo-600 px-4 py-2 rounded-md text-sm hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50"
              @click="confirmAddCash"
              :disabled="loading || addCashAmount === null || addCashAmount === 0"
            >
              <span v-if="!loading">ปรับเงิน</span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { projectFirestore } from '@/firebase/config';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import debounce from 'lodash/debounce';
import getUser from '@/composables/getUser';
import firebase from 'firebase/app';

dayjs.locale('th');

export default {
  setup() {
    const { user } = getUser();
    const router = useRouter();
    const users = ref([]);
    const searchQuery = ref('');
    const adminFilter = ref('');
    const characterFilter = ref('');
    const showBanModal = ref(false);
    const showUnbanModal = ref(false);
    const showAddCashModal = ref(false);
    const showIpUsersModal = ref(false);
    const selectedIp = ref('');
    const ipUsers = ref([]);
    const banUserId = ref('');
    const banUserName = ref('');
    const addCashUserId = ref('');
    const addCashUserName = ref('');
    const addCashCurrent = ref(0);
    const addCashAmount = ref(null);
    const addCashError = ref('');
    const loading = ref(false);
    const error = ref('');

    // ฟังก์ชันค้นหาแบบ debounce
    const fetchUsers = async () => {
      loading.value = true;
      error.value = '';
      try {
        let userData = [];
        if (searchQuery.value.trim()) {
          // ค้นหาโดย UID ก่อน
          const uidQuery = await projectFirestore
            .collection('users')
            .where('uid', '==', searchQuery.value.trim())
            .get();

          if (!uidQuery.empty) {
            // ถ้าพบผู้ใช้จาก UID
            const userDoc = uidQuery.docs[0];
            const data = userDoc.data();
            const user = {
              uid: userDoc.id,
              name: data.name || 'N/A',
              email: data.email || 'N/A',
              ip: data.ip || 'N/A',
              updated: data.updated || null,
              admin: data.admin || false,
              characterCreated: data.characterCreated || false,
              credit: data.credit || 0,
              cash: data.cash || 0,
              ban: data.ban || false,
              ipCount: 1,
            };

            // ถ้ามี IP ให้ค้นหาผู้ใช้ที่มี IP เดียวกัน
            if (user.ip && user.ip !== 'N/A') {
              const ipQuery = await projectFirestore
                .collection('users')
                .where('ip', '==', user.ip)
                .get();
              user.ipCount = ipQuery.size;
              userData = ipQuery.docs.map(doc => {
                const ipData = doc.data();
                return {
                  uid: doc.id,
                  name: ipData.name || 'N/A',
                  email: ipData.email || 'N/A',
                  ip: ipData.ip || 'N/A',
                  updated: ipData.updated || null,
                  admin: ipData.admin || false,
                  characterCreated: ipData.characterCreated || false,
                  credit: ipData.credit || 0,
                  cash: ipData.cash || 0,
                  ban: ipData.ban || false,
                  ipCount: ipQuery.size,
                };
              });
            } else {
              userData = [user];
            }
          } else {
            // ถ้าไม่พบ UID ให้ค้นหาด้วยชื่อหรืออีเมล
            const snapshot = await projectFirestore
              .collection('users')
              .orderBy('updated', 'desc')
              .limit(10)
              .get();
            userData = snapshot.docs.map(doc => {
              const data = doc.data();
              return {
                uid: doc.id,
                name: data.name || 'N/A',
                email: data.email || 'N/A',
                ip: data.ip || 'N/A',
                updated: data.updated || null,
                admin: data.admin || false,
                characterCreated: data.characterCreated || false,
                credit: data.credit || 0,
                cash: data.cash || 0,
                ban: data.ban || false,
                ipCount: 0,
              };
            });

            // นับจำนวนผู้ใช้ต่อ IP
            for (let user of userData) {
              if (user.ip && user.ip !== 'N/A') {
                const ipQuery = await projectFirestore
                  .collection('users')
                  .where('ip', '==', user.ip)
                  .get();
                user.ipCount = ipQuery.size;
              } else {
                user.ipCount = 1;
              }
            }
          }
        } else {
          // ถ้าไม่มี searchQuery ให้ดึงผู้ใช้ 10 คนล่าสุด
          const snapshot = await projectFirestore
            .collection('users')
            .orderBy('updated', 'desc')
            .limit(1)
            .get();
          userData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              uid: doc.id,
              name: data.name || 'N/A',
              email: data.email || 'N/A',
              ip: data.ip || 'N/A',
              updated: data.updated || null,
              admin: data.admin || false,
              characterCreated: data.characterCreated || false,
              credit: data.credit || 0,
              cash: data.cash || 0,
              ban: data.ban || false,
              ipCount: 0,
            };
          });

          // นับจำนวนผู้ใช้ต่อ IP
          for (let user of userData) {
            if (user.ip && user.ip !== 'N/A') {
              const ipQuery = await projectFirestore
                .collection('users')
                .where('ip', '==', user.ip)
                .get();
              user.ipCount = ipQuery.size;
            } else {
              user.ipCount = 1;
            }
          }
        }

        users.value = userData;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้: ' + err.message;
      } finally {
        loading.value = false;
      }
    };

    // Debounce การค้นหาเพื่อลดการเรียก API
    const debouncedSearch = debounce(fetchUsers, 500);

    // โหลดผู้ใช้เมื่อคอมโพเนนต์ถูกเมานต์
    onMounted(fetchUsers);

    // ฟังก์ชันกรองและค้นหาผู้ใช้
    const filteredUsers = computed(() => {
      let filtered = users.value;

      if (searchQuery.value && !users.value.some(u => u.uid === searchQuery.value.trim())) {
        const queryLower = searchQuery.value.toLowerCase();
        filtered = filtered.filter(user =>
          [user.name, user.email].some(field =>
            String(field).toLowerCase().includes(queryLower)
          )
        );
      }

      if (adminFilter.value !== '') {
        filtered = filtered.filter(user => user.admin === (adminFilter.value === 'true'));
      }

      if (characterFilter.value !== '') {
        filtered = filtered.filter(user => user.characterCreated === (characterFilter.value === 'true'));
      }

      return filtered;
    });

    // ฟังก์ชันแสดงผู้ใช้ที่มี IP เดียวกัน
    const openIpUsersModal = async (ip) => {
      if (!ip || ip === 'N/A') return;
      loading.value = true;
      selectedIp.value = ip;
      try {
        const ipQuery = await projectFirestore
          .collection('users')
          .where('ip', '==', ip)
          .get();
        ipUsers.value = ipQuery.docs.map(doc => {
          const data = doc.data();
          return {
            uid: doc.id,
            name: data.name || 'N/A',
            email: data.email || 'N/A',
          };
        });
        showIpUsersModal.value = true;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลผู้ใช้ที่มี IP เดียวกัน: ' + err.message;
      } finally {
        loading.value = false;
      }
    };

    // จัดรูปแบบ timestamp
    const formatTimestamp = (timestamp) => {
      if (!timestamp) return 'N/A';
      return dayjs(timestamp.toDate()).format('DD MMM YYYY HH:mm');
    };

    // เปิด Modal แบน
    const openBanModal = (userId, userName) => {
      banUserId.value = userId;
      banUserName.value = userName;
      showBanModal.value = true;
    };

    // เปิด Modal ปลดแบน
    const openUnbanModal = (userId, userName) => {
      banUserId.value = userId;
      banUserName.value = userName;
      showUnbanModal.value = true;
    };

    // เปิด Modal ปรับเงิน
    const openAddCashModal = (userId, userName, currentCash) => {
      addCashUserId.value = userId;
      addCashUserName.value = userName;
      addCashCurrent.value = currentCash;
      addCashAmount.value = null;
      addCashError.value = '';
      showAddCashModal.value = true;
    };

    // ดูตัวละครในแท็บใหม่
    const viewCharacter = (userId) => {
      const characterUrl = `/admin/users/${userId}/character`;
      window.open(characterUrl, '_blank');
    };

    // ยืนยันการแบน
    const confirmBan = async () => {
      loading.value = true;
      error.value = '';
      const callerUid = user.value.uid;
      try {
        // Check if caller is admin
        const callerDoc = await projectFirestore.collection('users').doc(callerUid).get();
        if (!callerDoc.exists || !callerDoc.data().admin) {
          throw new Error('เฉพาะแอดมินเท่านั้นที่สามารถแบนผู้ใช้ได้');
        }

        await projectFirestore.runTransaction(async (transaction) => {
          const userDocRef = projectFirestore.collection('users').doc(banUserId.value);
          const characterDocRef = projectFirestore.collection('characters').doc(banUserId.value);
          const characterPrivateDocRef = projectFirestore.collection('characterPrivate').doc(banUserId.value);
          const settingDocRef = projectFirestore.collection('server').doc('setting');

          const userDoc = await transaction.get(userDocRef);
          const characterDoc = await transaction.get(characterDocRef);
          const settingDoc = await transaction.get(settingDocRef);

          if (!userDoc.exists) {
            throw new Error('ไม่พบผู้ใช้');
          }

          transaction.update(userDocRef, {
            ban: true,
            updated: firebase.firestore.FieldValue.serverTimestamp(),
          });

          if (characterDoc.exists && characterDoc.data().isLive === true) {
            transaction.update(characterDocRef, { isLive: false });
            transaction.update(characterPrivateDocRef, { isLive: false });
            if (settingDoc.exists) {
              transaction.update(settingDocRef, {
                activePlayer: firebase.firestore.FieldValue.increment(-1),
              });
            }
          }

          const logRef = projectFirestore.collection('logs').doc();
          transaction.set(logRef, {
            uid: banUserId.value,
            action: 'ban_user',
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            details: { bannedBy: callerUid },
            displayMessage: 'ผู้ใช้ถูกแบน',
            success: true,
            error: null,
            functionName: 'banUser',
          });
        });

        users.value = users.value.map(u =>
          u.uid === banUserId.value ? { ...u, ban: true } : u
        );
        showBanModal.value = false;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการแบนผู้ใช้: ' + err.message;
      } finally {
        loading.value = false;
      }
    };

    // ยืนยันการปลดแบน
    const confirmUnban = async () => {
      loading.value = true;
      error.value = '';
      const callerUid = user.value.uid;
      try {
        // Check if caller is admin
        const callerDoc = await projectFirestore.collection('users').doc(callerUid).get();
        if (!callerDoc.exists || !callerDoc.data().admin) {
          throw new Error('เฉพาะแอดมินเท่านั้นที่สามารถปลดแบนผู้ใช้ได้');
        }

        await projectFirestore.runTransaction(async (transaction) => {
          const userDocRef = projectFirestore.collection('users').doc(banUserId.value);
          const characterDocRef = projectFirestore.collection('characters').doc(banUserId.value);
          const characterPrivateDocRef = projectFirestore.collection('characterPrivate').doc(banUserId.value);
          const settingDocRef = projectFirestore.collection('server').doc('setting');

          const userDoc = await transaction.get(userDocRef);
          const characterDoc = await transaction.get(characterDocRef);
          const settingDoc = await transaction.get(settingDocRef);

          if (!userDoc.exists) {
            throw new Error('ไม่พบผู้ใช้');
          }

          transaction.update(userDocRef, {
            ban: false,
            updated: firebase.firestore.FieldValue.serverTimestamp(),
          });

          if (characterDoc.exists) {
            transaction.update(characterDocRef, { isLive: true });
            transaction.update(characterPrivateDocRef, { isLive: true });
            if (settingDoc.exists) {
              transaction.update(settingDocRef, {
                activePlayer: firebase.firestore.FieldValue.increment(1),
              });
            }
          }

          const logRef = projectFirestore.collection('logs').doc();
          transaction.set(logRef, {
            uid: banUserId.value,
            action: 'unban_user',
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            details: { unbannedBy: callerUid },
            displayMessage: 'ผู้ใช้ถูกปลดแบน',
            success: true,
            error: null,
            functionName: 'unbanUser',
          });
        });

        users.value = users.value.map(u =>
          u.uid === banUserId.value ? { ...u, ban: false } : u
        );
        showUnbanModal.value = false;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการปลดแบนผู้ใช้: ' + err.message;
      } finally {
        loading.value = false;
      }
    };

    // ยืนยันการปรับเงิน
    const confirmAddCash = async () => {
      if (addCashAmount.value === null || addCashAmount.value === 0) {
        addCashError.value = 'กรุณากรอกจำนวนเงินที่ถูกต้อง (ไม่ใช่ศูนย์)';
        return;
      }

      const newCash = addCashCurrent.value + addCashAmount.value;
      if (newCash < 0) {
        addCashError.value = 'จำนวนเงินหลังปรับไม่สามารถติดลบได้';
        return;
      }

      loading.value = true;
      error.value = '';
      addCashError.value = '';
      const callerUid = user.value.uid;
      try {
        // Check if caller is admin
        const callerDoc = await projectFirestore.collection('users').doc(callerUid).get();
        if (!callerDoc.exists || !callerDoc.data().admin) {
          throw new Error('เฉพาะแอดมินเท่านั้นที่สามารถปรับเงินได้');
        }

        await projectFirestore.runTransaction(async (transaction) => {
          const userRef = projectFirestore.collection('users').doc(addCashUserId.value);
          const userDoc = await transaction.get(userRef);

          if (!userDoc.exists) {
            throw new Error('ไม่พบผู้ใช้');
          }

          const currentCash = userDoc.data().cash || 0;

          transaction.update(userRef, {
            cash: newCash,
            updated: firebase.firestore.FieldValue.serverTimestamp(),
          });

          const actionText = addCashAmount.value > 0 ? 'เพิ่ม' : 'หัก';
          const absAmount = Math.abs(addCashAmount.value);

          const logRef = projectFirestore.collection('logs').doc();
          transaction.set(logRef, {
            uid: addCashUserId.value,
            action: 'addCash',
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            details: {
              adminUid: callerUid,
              amount: addCashAmount.value,
              before: currentCash,
              after: newCash,
            },
            displayMessage: `แอดมิน ${callerUid} ${actionText} ${absAmount} แคชให้ผู้ใช้ ${addCashUserId.value} (จาก ${currentCash} เป็น ${newCash})`,
            success: true,
            error: null,
            functionName: 'addCash',
          });
        });

        users.value = users.value.map(u =>
          u.uid === addCashUserId.value
            ? { ...u, cash: u.cash + addCashAmount.value } : u
        );
        showAddCashModal.value = false;
      } catch (err) {
        addCashError.value = 'เกิดข้อผิดพลาดในการปรับเงิน: ' + err.message;
      } finally {
        loading.value = false;
      }
    };

    return {
      users,
      searchQuery,
      adminFilter,
      characterFilter,
      filteredUsers,
      formatTimestamp,
      showBanModal,
      showUnbanModal,
      showAddCashModal,
      showIpUsersModal,
      selectedIp,
      ipUsers,
      banUserId,
      banUserName,
      addCashUserId,
      addCashUserName,
      addCashCurrent,
      addCashAmount,
      addCashError,
      loading,
      error,
      openBanModal,
      openUnbanModal,
      openAddCashModal,
      openIpUsersModal,
      viewCharacter,
      confirmBan,
      confirmUnban,
      confirmAddCash,
      debouncedSearch,
    };
  },
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}

.overflow-x-auto {
  border-radius: 0.5rem;
}

table {
  border-collapse: collapse;
  width: 100%;
}

th,
td {
  padding: 12px;
}

thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

tr {
  transition: background-color 0.2s;
}

input,
select {
  transition: border-color 0.2s, ring-color 0.2s;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}
</style>